import { AccountClass, AccountSection, AccountType } from "./accounts";
import {
    accountTypeToAccountClass,
    isAccountType,
    orderedAccountClasses,
} from "./helpers";

// ── Section structure ──

interface ResolvedAccountGroups {
    sections: AccountSection[];
    type: AccountType;
}

const predefinedSectionPaths: Record<AccountType, AccountSection[]> = {
    [AccountType.cash]: [AccountSection.assets, AccountSection.currentAssets],
    [AccountType.accountsReceivable]: [
        AccountSection.assets,
        AccountSection.currentAssets,
    ],
    [AccountType.inventory]: [
        AccountSection.assets,
        AccountSection.currentAssets,
    ],
    [AccountType.otherCurrentAssets]: [
        AccountSection.assets,
        AccountSection.currentAssets,
    ],
    [AccountType.investments]: [
        AccountSection.assets,
        AccountSection.nonCurrentAssets,
    ],
    [AccountType.fixedAssets]: [
        AccountSection.assets,
        AccountSection.nonCurrentAssets,
    ],
    [AccountType.intangibleAssets]: [
        AccountSection.assets,
        AccountSection.nonCurrentAssets,
    ],
    [AccountType.otherAssets]: [
        AccountSection.assets,
        AccountSection.nonCurrentAssets,
    ],
    [AccountType.accountsPayable]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.liabilities,
    ],
    [AccountType.creditCards]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.liabilities,
    ],
    [AccountType.payrollLiabilities]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.liabilities,
    ],
    [AccountType.shortTermLoans]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.liabilities,
    ],
    [AccountType.otherCurrentLiabilities]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.liabilities,
    ],
    [AccountType.longTermLoans]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.liabilities,
    ],
    [AccountType.otherLiabilities]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.liabilities,
    ],
    [AccountType.equity]: [
        AccountSection.liabilitiesAndEquity,
        AccountSection.equity,
    ],
    [AccountType.income]: [],
    [AccountType.cogs]: [],
    [AccountType.operatingExpenses]: [],
    [AccountType.otherIncome]: [],
    [AccountType.otherExpenses]: [],
};

const resolveAccountGroups = (type: AccountType): ResolvedAccountGroups => ({
    sections: predefinedSectionPaths[type],
    type,
});

// ── Labels ──

export const getPredefinedGroupLabel = (
    group: AccountSection | AccountType,
): string => (group === AccountType.cogs ? "Cost of Goods Sold" : group);

export const resolveLeafType = (
    type: string | null | undefined,
): AccountType | null => {
    if (!type || !isAccountType(type)) {
        return null;
    }
    return resolveAccountGroups(type).type;
};

export const getLeafGroupLabel = (type: string): string => {
    const leafType = resolveLeafType(type);
    return leafType ? getPredefinedGroupLabel(leafType) : type;
};

export const getPredefinedGroupTierLabels = (type: AccountType): string[] => {
    const resolved = resolveAccountGroups(type);
    return [
        ...resolved.sections.map(getPredefinedGroupLabel),
        getPredefinedGroupLabel(resolved.type),
    ];
};

// ── Depth limits ──

// Only business-owned groups count; predefined report tiers do not.
export const MAX_ACCOUNT_GROUP_DEPTH = 5;

export interface AccountHierarchyDepthCheck {
    depth: number;
    maxDepth: number;
    isWithinLimit: boolean;
}

export interface CheckAccountHierarchyDepthParams {
    type: AccountType;
    accountGroupChainDepth: number;
}

export const checkAccountHierarchyDepth = ({
    type,
    accountGroupChainDepth,
}: CheckAccountHierarchyDepthParams): AccountHierarchyDepthCheck => {
    if (!isAccountType(type)) {
        throw new Error(
            `Account groups are only supported for live V2 account types; got "${type}".`,
        );
    }
    return {
        depth: accountGroupChainDepth,
        maxDepth: MAX_ACCOUNT_GROUP_DEPTH,
        isWithinLimit: accountGroupChainDepth <= MAX_ACCOUNT_GROUP_DEPTH,
    };
};

// ── Candidate parent depth gating ──

export interface AccountGroupNode {
    id: string;
    parentGroupId: string | null;
    type: AccountType;
}

interface DepthExceedingParentParams {
    groups: AccountGroupNode[];
    // The group being reparented, including its existing sub-group subtree.
    // Omit when creating a new group (it has no subtree yet).
    movedGroupId?: string | null;
}

const getGroupChainDepth = (
    groupsById: Map<string, AccountGroupNode>,
    fromId: string,
): number => {
    let depth = 0;
    const visited = new Set<string>();
    let cursor = groupsById.get(fromId);
    while (cursor && cursor.parentGroupId !== null) {
        if (visited.has(cursor.id)) {
            break;
        }
        visited.add(cursor.id);
        cursor = groupsById.get(cursor.parentGroupId);
        depth += 1;
    }
    return depth;
};

const getMaxSubtreeDepth = (
    childrenByParent: Map<string, AccountGroupNode[]>,
    fromId: string,
    visited: Set<string> = new Set(),
): number => {
    if (visited.has(fromId)) {
        return 0;
    }
    visited.add(fromId);
    const children = childrenByParent.get(fromId) ?? [];
    if (children.length === 0) {
        return 0;
    }
    return (
        1 +
        Math.max(
            ...children.map((child) =>
                getMaxSubtreeDepth(childrenByParent, child.id, visited),
            ),
        )
    );
};

// Mirrors backend depth checks so invalid picker options can be disabled early.
export const findDepthExceedingParentGroupIds = ({
    groups,
    movedGroupId,
}: DepthExceedingParentParams): Set<string> => {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const childrenByParent = new Map<string, AccountGroupNode[]>();
    for (const group of groups) {
        if (group.parentGroupId === null) {
            continue;
        }
        const siblings = childrenByParent.get(group.parentGroupId) ?? [];
        siblings.push(group);
        childrenByParent.set(group.parentGroupId, siblings);
    }

    const movedSubtreeDepth = movedGroupId
        ? getMaxSubtreeDepth(childrenByParent, movedGroupId)
        : 0;

    const exceedingIds = new Set<string>();
    for (const candidate of groups) {
        if (!resolveLeafType(candidate.type)) {
            continue;
        }
        const accountGroupChainDepth =
            getGroupChainDepth(groupsById, candidate.id) +
            1 +
            1 +
            movedSubtreeDepth;
        const { isWithinLimit } = checkAccountHierarchyDepth({
            type: candidate.type,
            accountGroupChainDepth,
        });
        if (!isWithinLimit) {
            exceedingIds.add(candidate.id);
        }
    }
    return exceedingIds;
};

// Reuses picker depth semantics so inline, modal, and backend validation agree.
interface CanNestGroupUnderParentParams {
    groups: AccountGroupNode[];
    parentGroupId: string | null;
}

export const canNestGroupUnderParent = ({
    groups,
    parentGroupId,
}: CanNestGroupUnderParentParams): boolean => {
    if (parentGroupId === null) {
        return true;
    }
    return !findDepthExceedingParentGroupIds({ groups }).has(parentGroupId);
};

// ── Tree building ──

export interface SectionTreeBuilder<TNode> {
    createSectionNode: (section: AccountSection, pathKey: string) => TNode;
    getSectionChildren: (sectionNode: TNode) => TNode[];
    // Return null when the leaf has no row to place.
    createLeafNode: (leafType: AccountType) => TNode | null;
}

export const nestLeavesUnderSections = <TNode>(
    leafTypes: readonly AccountType[],
    builder: SectionTreeBuilder<TNode>,
): TNode[] => {
    const topLevel: TNode[] = [];
    const sectionNodeByKey = new Map<string, TNode>();
    for (const leafType of leafTypes) {
        const leafNode = builder.createLeafNode(leafType);
        if (leafNode === null) {
            continue;
        }
        let parentChildren = topLevel;
        let pathKey = "";
        for (const section of resolveAccountGroups(leafType).sections) {
            pathKey = pathKey ? `${pathKey}/${section}` : section;
            const existing = sectionNodeByKey.get(pathKey);
            if (existing) {
                parentChildren = builder.getSectionChildren(existing);
            } else {
                const sectionNode = builder.createSectionNode(section, pathKey);
                sectionNodeByKey.set(pathKey, sectionNode);
                parentChildren.push(sectionNode);
                parentChildren = builder.getSectionChildren(sectionNode);
            }
        }
        parentChildren.push(leafNode);
    }
    return topLevel;
};

// ── Ordering ──

export const orderedPredefinedLeafTypes: AccountType[] = [
    AccountType.cash,
    AccountType.accountsReceivable,
    AccountType.inventory,
    AccountType.otherCurrentAssets,
    AccountType.investments,
    AccountType.fixedAssets,
    AccountType.intangibleAssets,
    AccountType.otherAssets,
    AccountType.accountsPayable,
    AccountType.creditCards,
    AccountType.payrollLiabilities,
    AccountType.shortTermLoans,
    AccountType.otherCurrentLiabilities,
    AccountType.longTermLoans,
    AccountType.otherLiabilities,
    AccountType.equity,
    AccountType.income,
    AccountType.cogs,
    AccountType.operatingExpenses,
    AccountType.otherIncome,
    AccountType.otherExpenses,
];

const leafTypeOrderByType = new Map<AccountType, number>(
    orderedPredefinedLeafTypes.map((type, index) => [type, index]),
);

// Keeps same-group accounts contiguous in group-aware pickers.
export const compareAccountsByLeafGroup = (
    a: { type: string },
    b: { type: string },
): number => {
    const orderOf = (type: string): number => {
        const leafType = resolveLeafType(type);
        return leafType
            ? (leafTypeOrderByType.get(leafType) ?? Number.MAX_SAFE_INTEGER)
            : Number.MAX_SAFE_INTEGER;
    };
    return orderOf(a.type) - orderOf(b.type);
};

// ── Sort order ──

// sortOrder used for a group with no descendant accounts, anchoring it after
// every code-anchored row in its scope (mirrors the AddAccountSortOrder
// backfill; numeric(20,10) caps at 10 integer digits so this is the max).
export const EMPTY_GROUP_SORT_ORDER_SENTINEL = 9999999999;

export interface AccountTypeClassGroup {
    accountClass: AccountClass;
    types: AccountType[];
}

export const groupAccountTypesByClass = (
    types: readonly AccountType[],
): AccountTypeClassGroup[] => {
    const typesByClass = new Map<AccountClass, AccountType[]>();
    for (const type of types) {
        const accountClass = accountTypeToAccountClass[type];
        const existing = typesByClass.get(accountClass);
        if (existing) {
            existing.push(type);
        } else {
            typesByClass.set(accountClass, [type]);
        }
    }
    return orderedAccountClasses
        .map((accountClass) => ({
            accountClass,
            types: typesByClass.get(accountClass) ?? [],
        }))
        .filter((group) => group.types.length > 0);
};

// ── Picker grouping ──

// Assumes items are already ordered so same-group items are adjacent.
export const withGroupHeaders = <TItem extends object>(
    items: readonly TItem[],
    groupKeyOf: (item: TItem) => string,
    groupLabelOf: (item: TItem) => string,
): Array<TItem & { groupHeader: string | undefined }> =>
    items.map((item, index, allItems) => {
        const isFirstOfGroup =
            index === 0 || groupKeyOf(allItems[index - 1]) !== groupKeyOf(item);
        return {
            ...item,
            groupHeader: isFirstOfGroup ? groupLabelOf(item) : undefined,
        };
    });

export const leafGroupHeader = {
    keyOf: (account: { type: string }): string =>
        resolveLeafType(account.type) ?? account.type,
    labelOf: (account: { type: string }): string =>
        getLeafGroupLabel(account.type),
};
